import { PERMISSIONS } from '@ibe/constants';
import { z } from 'zod';

/**
 * The Roles module's contracts.
 *
 * Types come from `z.infer`, never hand-written, so a schema and its type
 * cannot drift. Every limit is the one the service enforces, so an over-long
 * name is a sentence beside the input rather than a round trip and a 422 — the
 * server still validates, this is a courtesy.
 */

const NAME_MAX = 200;
const DESCRIPTION_MAX = 500;

/**
 * One row in the list.
 *
 * Only `id` is guaranteed. The list sends `?fields=` derived from the columns
 * the reader has chosen and the service honours it strictly, so a field behind
 * a hidden column is genuinely absent.
 */
export const roleRow = z.object({
  id: z.string(),
  name: z.string().optional(),
  description: z.string().nullable().optional(),
  isSystem: z.boolean().optional(),
});

export type RoleRow = z.infer<typeof roleRow>;

/**
 * The full role, as the detail screen and the edit form read it.
 *
 * Required rather than optional: the detail endpoint is called without
 * `?fields=` and therefore projects everything, so a missing key is a contract
 * change worth failing on.
 */
export const roleDetail = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  isSystem: z.boolean(),
  permissions: z.array(z.string()),
});

export type RoleDetail = z.infer<typeof roleDetail>;

/**
 * What the create and edit forms collect.
 *
 * One schema for both, because they are the same fields under the same rules;
 * what differs is which of them the service will accept, and that is the
 * payload builder's job rather than a second schema to keep in step.
 */
export const roleFormInput = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'A role needs a name — it is what an operator picks it by')
    .max(NAME_MAX, `Keep the name under ${String(NAME_MAX)} characters`),
  description: z
    .string()
    .trim()
    .max(DESCRIPTION_MAX, `Keep the description under ${String(DESCRIPTION_MAX)} characters`)
    .optional(),
  permissions: z.array(z.string()),
});

export type RoleFormInput = z.infer<typeof roleFormInput>;

/** What the create endpoint returns. */
export const createdRole = roleDetail;

export type CreatedRole = z.infer<typeof createdRole>;

/** Permission labels the portal uses when an operator creates or edits a role. */
export const ROLE_WRITE = PERMISSIONS.ROLE_WRITE;
export const ROLE_READ = PERMISSIONS.ROLE_READ;
